import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react/dist/iconify.js';
import apiRequest from '../../../api/request';
import RetentionThemeProvider from '../theme/RetentionThemeProvider';
import { HERO_GRADIENT, RT } from '../theme/retentionTheme';
import { EXIT_FIELDS, LEVEL_LABELS, STAGE_FIELDS } from '../constants/stageFields';
import { MAPPING_STATUS, PHASES, RETENTION_STAGES, getWorkflowStageNo, stageByNo } from '../constants/stages';
import StageStepper from './StageStepper';
import { EmptyState, IconBadge, SectionCard, SoftChip, StatTile } from './ui';

// Read-only "everything the workflow collected" for one enrolment, opened from CRO Leads (View). It reads the same
// endpoints as the workspace (header, stages, profile, demo, tracking, timeline) into local state, so it never
// disturbs an open workspace, and renders every stage from constants/stageFields.js — the same labels and options
// the forms use.

const READS = ['header', 'stages', 'profile', 'demo', 'tracking', 'timeline'];
const EMPTY = { data: null, error: null };

const fmtDate = (v, withTime) => (v ? dayjs(v).format(withTime ? 'DD MMM YYYY, hh:mm A' : 'DD MMM YYYY') : null);

/** A field's value as text, the way the form would show it; null when nothing was recorded. */
const display = (field, raw, dynamicOptions) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (Array.isArray(raw)) return raw.length ? raw.join(' · ') : null;
  const options = field.optionsFrom ? dynamicOptions?.[field.optionsFrom] || [] : field.options || [];
  const label = (v) => options.find((o) => String(o.value) === String(v))?.label ?? v;
  switch (field.type) {
    case 'chips':
    case 'select':
      return label(raw);
    case 'multi':
      return String(raw).split(',').filter(Boolean).map(label).join(' · ') || null;
    case 'level':
      return `${raw} · ${LEVEL_LABELS[Number(raw) - 1] || ''}`;
    case 'yesno':
      return Number(raw) === 1 ? 'Yes' : 'No';
    case 'datetime':
      return fmtDate(raw, true);
    case 'date':
      return fmtDate(raw);
    default: {
      // DECIMAL columns come back as "90.00"
      const text = /^-?\d+\.\d+$/.test(String(raw)) ? String(Number(raw)) : String(raw);
      return field.suffix ? `${text}${field.suffix}` : text;
    }
  }
};

function FieldGrid({ sections, values, dynamicOptions, showEmpty }) {
  const blocks = sections
    .map((section) => ({
      section,
      rows: section.fields
        .map((f) => ({ f, text: display(f, values?.[f.key], dynamicOptions) }))
        .filter((r) => showEmpty || r.text !== null),
    }))
    .filter((b) => b.rows.length);
  if (!blocks.length) return <Typography variant="caption" sx={{ color: RT.faint }}>Nothing recorded</Typography>;
  return (
    <Stack gap={1.5}>
      {blocks.map(({ section, rows }) => (
        <Box key={section.title}>
          <Stack direction="row" alignItems="center" gap={0.75} mb={0.75}>
            <Icon icon={section.icon} width={16} color={section.private ? RT.error : RT.primary} />
            <Typography variant="overline" sx={{ color: section.private ? RT.error : RT.ink2, lineHeight: 1.6 }}>
              {section.title}
            </Typography>
            {section.private && <SoftChip label="CRO only" icon="solar:lock-keyhole-bold" color={RT.error} sx={{ height: 18, fontSize: 10 }} />}
          </Stack>
          <Grid container columnSpacing={2} rowSpacing={0.75}>
            {rows.map(({ f, text }) => (
              <Grid item xs={12} sm={f.type === 'textarea' ? 12 : 6} md={f.type === 'textarea' ? 12 : 4} key={f.key}>
                <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 700, display: 'block' }}>
                  {f.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: text ? RT.ink : RT.faint, whiteSpace: 'pre-line' }}>
                  {text ?? '—'}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Stack>
  );
}

// ---- KYC profile (RETENTION_WORKFLOW.md §5.6): every field marked `kyc` in stageFields, grouped by the four columns
const KYC_GROUPS = [
  { key: 'Academic', icon: 'solar:square-academic-cap-bold-duotone', color: '#2563EB' },
  { key: 'Demographic', icon: 'solar:users-group-rounded-bold-duotone', color: '#0E7C86' },
  { key: 'Psychographic', icon: 'solar:brain-bold-duotone', color: '#7C3AED' },
  { key: 'Economic', icon: 'solar:wallet-money-bold-duotone', color: RT.warning, private: true },
];
const KYC_FIELDS = Object.entries(STAGE_FIELDS).flatMap(([no, cfg]) =>
  (cfg.sections || []).flatMap((s) => s.fields.filter((f) => f.kyc).map((f) => ({ ...f, stageNo: Number(no) }))),
);

function KycProfile({ values, dynamicOptions, onFill }) {
  const groups = KYC_GROUPS.map((g) => {
    const rows = KYC_FIELDS.filter((f) => f.kyc === g.key).map((f) => ({ f, text: display(f, values[f.key], dynamicOptions) }));
    return { ...g, rows, filled: rows.filter((r) => r.text !== null).length };
  });
  const filled = groups.reduce((a, g) => a + g.filled, 0);
  const total = groups.reduce((a, g) => a + g.rows.length, 0);
  return (
    <SectionCard
      icon="solar:user-id-bold-duotone"
      color="#7C3AED"
      title="KYC profile"
      action={
        <Stack direction="row" gap={1} alignItems="center">
          <SoftChip label={`${filled} of ${total} filled`} color={filled === total ? RT.success : RT.warning} />
          {filled < total && onFill && (
            <Button size="small" variant="outlined" onClick={onFill} startIcon={<Icon icon="solar:pen-bold" width={14} />}>
              Fill
            </Button>
          )}
        </Stack>
      }
    >
      <Grid container spacing={2}>
        {groups.map((g) => (
          <Grid item xs={12} md={6} key={g.key}>
            <Box sx={{ p: 1.5, borderRadius: 3, border: `1px solid ${RT.line}`, height: '100%' }}>
              <Stack direction="row" alignItems="center" gap={1} mb={1}>
                <IconBadge icon={g.icon} color={g.color} size={28} />
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {g.key}
                </Typography>
                {g.private && <SoftChip label="CRO only" icon="solar:lock-keyhole-bold" color={RT.error} sx={{ height: 20, fontSize: 10.5 }} />}
                <SoftChip label={`${g.filled}/${g.rows.length}`} color={g.filled === g.rows.length ? RT.success : g.color} sx={{ height: 20, fontSize: 10.5 }} />
              </Stack>
              <Stack divider={<Box sx={{ borderTop: `1px dashed ${RT.line}` }} />}>
                {g.rows.map(({ f, text }) => (
                  <Stack key={f.key} direction="row" alignItems="baseline" gap={1} sx={{ py: 0.6 }}>
                    <Typography variant="caption" sx={{ color: RT.muted, fontWeight: 700, flex: '0 0 46%' }}>
                      {f.label}
                      {f.source === 'auto' && <Icon icon="solar:bolt-bold" width={11} color={RT.info} style={{ marginLeft: 4 }} />}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: text ? RT.ink : RT.faint, whiteSpace: 'pre-line', minWidth: 0 }}>
                      {text ?? '—'}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>
    </SectionCard>
  );
}

function StageBlock({ stageNo, progress, currentStage, children }) {
  const stage = stageByNo(stageNo);
  const phase = PHASES[stage.phase];
  const status = progress?.stage_status;
  const done = status === 2 || (status === undefined && stageNo < currentStage);
  const now = stageNo === currentStage;
  return (
    <Box sx={{ p: 1.75, borderRadius: 3, border: `1px solid ${now ? alpha(phase.color, 0.45) : RT.line}`, bgcolor: RT.paper }}>
      <Stack direction="row" alignItems="center" gap={1.25} mb={1.25}>
        <IconBadge icon={done ? 'solar:check-circle-bold' : stage.icon} color={done ? RT.success : phase.color} size={32} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Stage {stageNo} · {stage.short}
        </Typography>
        {now && <SoftChip label="Now" color={phase.color} />}
        {done && <SoftChip label="Done" icon="solar:check-read-linear" color={RT.success} />}
        {progress?.updated_by_name && (
          <Typography variant="caption" sx={{ color: RT.muted }}>
            {progress.updated_by_name} · {fmtDate(progress.updated_on)}
          </Typography>
        )}
      </Stack>
      {children}
    </Box>
  );
}

export default function StudentDataDialog({ open, onClose, courseEnrollmentId, row }) {
  const navigate = useNavigate();
  const [state, setState] = useState(() => Object.fromEntries(READS.map((k) => [k, EMPTY])));
  const [loading, setLoading] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);

  const load = useCallback(async () => {
    if (!courseEnrollmentId) return;
    setLoading(true);
    const results = await Promise.all(
      READS.map((k) => apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/${k}` }).catch((e) => ({ status: 'error', message: e.message }))),
    );
    setState(
      Object.fromEntries(
        READS.map((k, i) => {
          const res = results[i];
          return [k, res?.status === 'success' ? { data: res.data, error: null } : { data: null, error: res?.message || 'Not available' }];
        }),
      ),
    );
    setLoading(false);
  }, [courseEnrollmentId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const header = state.header.data || {};
  const student = { ...(row || {}), ...header };
  const currentStage = getWorkflowStageNo(student);
  const progress = useMemo(() => {
    const map = {};
    (Array.isArray(state.stages.data) ? state.stages.data : []).forEach((s) => {
      if (s.stage_status !== null && s.stage_status !== undefined) map[s.stage_no] = s;
    });
    return map;
  }, [state.stages.data]);

  const profile = state.profile.data?.profile || {};
  const demo = state.demo.data;
  const tracking = state.tracking.data;
  const dynamicOptions = {
    ...(state.profile.data?.options || {}),
    ...(tracking?.options || {}),
    ...(demo?.options || {}),
  };
  const subjects = demo?.subjects || [];
  const signals = tracking?.signals || {};
  const blocked = state.header.error && !state.header.data;
  const schemaNote = [state.stages, state.profile, state.demo, state.tracking].find((r) => r.error)?.error;

  const stageBody = (no) => {
    const cfg = STAGE_FIELDS[no];
    const grid = (values, sections = cfg.sections) => (
      <FieldGrid sections={sections} values={values} dynamicOptions={dynamicOptions} showEmpty={showEmpty} />
    );
    if (no <= 3) return grid({ ...student, ...profile });
    if (no === 4) {
      return (
        <Stack gap={0.75}>
          {(demo?.attempts || []).length === 0 ? (
            <Typography variant="caption" sx={{ color: RT.faint }}>No tutor mapped yet</Typography>
          ) : (
            demo.attempts.map((a) => (
              <Stack key={a.retention_demo_attempt_id} direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <SoftChip label={MAPPING_STATUS[a.mapping_status]?.label} color={[RT.faint, RT.warning, RT.success, RT.error][a.mapping_status]} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {a.subject_name} · {a.trainer_name || 'awaiting tutor'} · attempt {a.attempt_no}
                </Typography>
                <Typography variant="caption" sx={{ color: RT.muted }}>
                  {[a.preferred_days, a.preferred_time_slot, a.session_duration && `${a.session_duration} min`, fmtDate(a.mapped_at)].filter(Boolean).join(' · ')}
                </Typography>
                {(a.rejection_reason || a.tutor_response_reason) && (
                  <Typography variant="caption" sx={{ color: RT.error }}>
                    {a.rejection_reason ? `Parent: ${a.rejection_reason}` : `Tutor declined: ${a.tutor_response_reason}`}
                  </Typography>
                )}
              </Stack>
            ))
          )}
        </Stack>
      );
    }
    if (no === 5 || no === 6) {
      const latest = subjects.map((s) => s.latest_attempt && { ...s.latest_attempt, subject_name: s.subject_name }).filter(Boolean);
      if (!latest.length) return <Typography variant="caption" sx={{ color: RT.faint }}>No demo yet</Typography>;
      return (
        <Stack gap={1.5}>
          {latest.map((a) => (
            <Box key={a.retention_demo_attempt_id}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: '#7C3AED' }}>
                {a.subject_name} · {a.trainer_name || 'Tutor'} · attempt {a.attempt_no}
                {no === 5 && a.tutor_response !== null && a.tutor_response !== undefined
                  ? ` · tutor ${Number(a.tutor_response) === 1 ? 'accepted' : 'declined'}`
                  : ''}
              </Typography>
              {grid(a)}
            </Box>
          ))}
        </Stack>
      );
    }
    if (no === 7) {
      const ck = demo?.checklist || {};
      const values = Object.fromEntries(
        Object.entries(ck).map(([k, v]) => [k, v ? `Done · ${[v.by, fmtDate(v.at)].filter(Boolean).join(' · ')}` : null]),
      );
      values.first_session_date = fmtDate(demo?.first_session_date);
      const sections = cfg.sections.map((s) => ({ ...s, fields: s.fields.map((f) => ({ ...f, type: 'readonly' })) }));
      return grid(values, sections);
    }
    if (no === 8) return grid({ ...signals, at_risk_indicators: signals.at_risk_indicators, ...(tracking?.firstFeedback || {}) });
    if (cfg.reportType) {
      const report = tracking?.reports?.[cfg.reportType];
      return (
        <Stack gap={1}>
          {report?.shared_with_parent_at && (
            <Typography variant="caption" sx={{ color: RT.muted }}>
              Shared {fmtDate(report.shared_with_parent_at, true)}
              {report.parent_viewed_at ? ` · viewed ${fmtDate(report.parent_viewed_at)}` : ' · not viewed yet'}
            </Typography>
          )}
          {grid({ ...(no === 9 || no === 11 ? signals : {}), ...(report || {}) })}
        </Stack>
      );
    }
    if (no === 12) {
      const calls = tracking?.calls || [];
      const exit = tracking?.exit;
      return (
        <Stack gap={1.5}>
          {calls.length === 0 && !exit && <Typography variant="caption" sx={{ color: RT.faint }}>No calls logged</Typography>}
          {calls.map((c, i) => (
            <Box key={c.retention_parent_touchpoint_id}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: RT.primary }}>
                Call {calls.length - i} · {fmtDate(c.created_at, true)}
              </Typography>
              {grid(c)}
            </Box>
          ))}
          {exit && (
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(RT.error, 0.05) }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: RT.error }}>
                Exit · {EXIT_FIELDS[exit.touchpoint_type]?.title} · {fmtDate(exit.created_at)}
              </Typography>
              {grid(exit, EXIT_FIELDS[exit.touchpoint_type]?.sections || [])}
            </Box>
          )}
        </Stack>
      );
    }
    if (no === 13) {
      const r = tracking?.renewal || {};
      return grid({ ...r, extra_sessions: r.last?.extra_sessions, extra_sale_amount: r.last?.extra_sale_amount });
    }
    return null;
  };

  const phases = [1, 2, 3].map((p) => ({ phase: PHASES[p], stages: RETENTION_STAGES.filter((s) => s.phase === p) }));
  const name = student.name || student.student_name || 'Student';

  return (
    // The theme provider sits outside the Dialog: inside it, its wrapper Box would come between the Paper and
    // DialogContent and break the Paper's flex column, so the content could not scroll.
    <RetentionThemeProvider>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper" PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        {/* hero */}
        <Box sx={{ background: HERO_GRADIENT, color: '#fff', px: 3, py: 2.25 }}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconBadge icon="solar:user-id-bold-duotone" color="#fff" size={52} sx={{ bgcolor: 'rgba(255,255,255,.18)', color: '#fff' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" sx={{ opacity: 0.8 }}>
                Workflow data
              </Typography>
              <Typography variant="h5" sx={{ color: '#fff' }} noWrap>
                {name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {[student.admn_no, student.class, student.syllabus, student.assigned_course_name, student.cro_name && `CRO ${student.cro_name}`]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
            <SoftChip
              label={`Stage ${currentStage} · ${stageByNo(currentStage)?.short}`}
              icon={stageByNo(currentStage)?.icon}
              color="#fff"
              sx={{ bgcolor: 'rgba(255,255,255,.18)' }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/student-journey/${courseEnrollmentId}`, { state: { row } })}
              sx={{ bgcolor: '#fff', color: RT.primary, '&:hover': { bgcolor: '#EEF0FF' } }}
              startIcon={<Icon icon="mdi:open-in-new" width={16} />}
            >
              Open workspace
            </Button>
            <IconButton onClick={onClose} sx={{ color: '#fff' }}>
              <Icon icon="solar:close-circle-bold" width={26} />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent dividers sx={{ bgcolor: RT.surface, p: { xs: 1.5, md: 2.5 }, overflowY: 'auto' }}>
          {loading && !state.header.data ? (
            <EmptyState icon="svg-spinners:ring-resize" title="Loading…" />
          ) : blocked ? (
            <Alert severity="warning">{state.header.error}</Alert>
          ) : (
            <Stack gap={2.5}>
              {schemaNote && <Alert severity="info">{schemaNote}</Alert>}

              {/* sessions at a glance */}
              <Stack direction="row" gap={2} flexWrap="wrap">
                <StatTile icon="solar:layers-bold-duotone" color="#2563EB" label="Sessions done" value={`${student.sessions_done ?? signals.sessions_done ?? 0} / ${signals.sessions_total || student.total_session_count || '—'}`} />
                <StatTile icon="solar:check-square-bold-duotone" color={RT.success} label="Attendance" value={student.attendance_percent != null ? `${student.attendance_percent}%` : '—'} />
                <StatTile icon="solar:graph-up-bold-duotone" color="#7C3AED" label="4-week consistency" value={signals.consistency_percent != null ? `${signals.consistency_percent}%` : '—'} />
                <StatTile icon="solar:calendar-bold-duotone" color={RT.warning} label="Last class" value={signals.days_since_last_session != null ? `${signals.days_since_last_session} days ago` : '—'} />
                <StatTile
                  icon="solar:wallet-money-bold-duotone"
                  color="#0E7C86"
                  label="Fee collected"
                  value={student.currency_type === 'AED' ? `AED ${student.collected_fee_aed ?? 0}` : `₹${student.collected_fee_inr ?? 0}`}
                  hint={student.total_sales_amount ? `of ${student.total_sales_amount}` : undefined}
                />
              </Stack>
              {(signals.at_risk_indicators || []).length > 0 && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {signals.at_risk_indicators.map((f) => (
                    <SoftChip key={f} label={f} icon="solar:danger-triangle-bold" color={RT.error} />
                  ))}
                </Stack>
              )}

              <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: RT.paper, border: `1px solid ${RT.line}` }}>
                <StageStepper currentStage={currentStage} progress={progress} />
              </Box>

              {/* KYC: stages 1–3 profile, the demo's communication baseline, derived attendance / at-risk / gap */}
              <KycProfile
                values={{
                  ...student,
                  ...profile,
                  communication_skill_baseline: subjects.map((s) => s.latest_attempt?.communication_skill_baseline).find((v) => v != null),
                  ...signals,
                  // an empty list is a result ("no flags"), not a gap
                  at_risk_indicators: Array.isArray(signals.at_risk_indicators) && !signals.at_risk_indicators.length ? 'None' : signals.at_risk_indicators,
                }}
                dynamicOptions={dynamicOptions}
                onFill={() => navigate(`/student-journey/${courseEnrollmentId}/onboarding`, { state: { row } })}
              />

              {/* subjects & sessions */}
              <SectionCard icon="solar:book-2-bold-duotone" title="Subjects & sessions">
                {subjects.length === 0 ? (
                  <EmptyState icon="solar:book-bookmark-linear" title="No subjects enrolled" compact />
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['Subject', 'Sessions', 'Per week', 'Start', 'First session', 'Preference', 'Tutor', 'Mapping'].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 800, color: RT.muted, whiteSpace: 'nowrap' }}>
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subjects.map((s) => (
                          <TableRow key={s.subject_enrollment_id}>
                            <TableCell sx={{ fontWeight: 700 }}>{s.subject_name}</TableCell>
                            <TableCell>{s.total_sessions ?? '—'}</TableCell>
                            <TableCell>{s.cro_class_per_week ?? '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(s.session_start_date) || '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {s.firstsession_starttime ? `${String(s.firstsession_starttime).slice(0, 5)}–${String(s.firstsession_endtime || '').slice(0, 5)}` : '—'}
                            </TableCell>
                            <TableCell>
                              {s.preferred_days ? `${s.preferred_days.replace(/,/g, ' ')} · ${s.preferred_time_slot}` : <SoftChip label="Not added" color={RT.warning} />}
                            </TableCell>
                            <TableCell>{s.trainer_name || '—'}</TableCell>
                            <TableCell>
                              {s.latest_attempt ? (
                                <SoftChip
                                  label={`${MAPPING_STATUS[s.latest_attempt.mapping_status]?.label} · #${s.latest_attempt.attempt_no}`}
                                  color={[RT.faint, RT.warning, RT.success, RT.error][s.latest_attempt.mapping_status]}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </SectionCard>

              <Stack direction="row" justifyContent="flex-end">
                <FormControlLabel
                  control={<Switch size="small" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} />}
                  label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Show empty fields</Typography>}
                />
              </Stack>

              {/* every stage, by phase */}
              {phases.map(({ phase, stages }) => (
                <Box key={phase.label}>
                  <Stack direction="row" alignItems="center" gap={1} mb={1.25}>
                    <IconBadge icon={phase.icon} color={phase.color} size={30} />
                    <Typography variant="subtitle1" sx={{ color: phase.color, fontWeight: 800 }}>
                      {phase.label}
                    </Typography>
                    <Box sx={{ flex: 1, borderTop: `1px dashed ${alpha(phase.color, 0.35)}` }} />
                  </Stack>
                  <Stack gap={1.5}>
                    {stages.map((s) => (
                      <StageBlock key={s.no} stageNo={s.no} progress={progress[s.no]} currentStage={currentStage}>
                        {stageBody(s.no)}
                      </StageBlock>
                    ))}
                  </Stack>
                </Box>
              ))}

              {/* history */}
              <SectionCard icon="solar:history-bold-duotone" title="Recent history">
                {(state.timeline.data || []).length === 0 ? (
                  <EmptyState icon="solar:history-linear" title="No history yet" compact />
                ) : (
                  <Stack gap={0.75}>
                    {state.timeline.data.slice(0, 15).map((e, i) => (
                      <Stack key={i} direction="row" gap={1.25} alignItems="baseline">
                        <Typography variant="caption" sx={{ color: RT.muted, minWidth: 140 }}>
                          {fmtDate(e.event_at, true)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                          {e.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: RT.muted }}>
                          {[e.actor_name, e.actor_type].filter(Boolean).join(' · ')}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </SectionCard>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </RetentionThemeProvider>
  );
}
